import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  AlertCircle, 
  Search, 
  X,
  Truck,
  Package,
  BarChart3,
  User,
  LogOut
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

export default function DailyRunSheet({ 
  shift = 'Morning', 
  selectedDate = '2026-09-18',
  centers = [], 
  lines = [], 
  products = [], 
  customers = [], 
  deliveries = [], 
  onUpdateDelivery,
  currentTenant,
  lang = 'mr',
  features,
  currentUser,
  onLogout
}) {
  const isMr = lang === 'mr';
  const isDeliveryRole = currentUser?.role === 'DELIVERY';
  const effectiveFeatures = features || currentTenant?.features || currentTenant?.feature || {};
  const isDairyEnabled = Boolean(effectiveFeatures.dairy_byproducts);
  const isBakeryEnabled = Boolean(effectiveFeatures.bakery_products);
  const hasExtraProducts = isDairyEnabled || isBakeryEnabled;

  const tenantSlug = currentTenant?.slug || (() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? JSON.parse(saved)?.slug : 'sachin-milk-shop';
    } catch {
      return 'sachin-milk-shop';
    }
  })();

  // Delivery Boy internal sub-tab: 'runsheet' | 'orders' | 'summary' | 'profile'
  const [deliveryTab, setDeliveryTab] = useState('runsheet');

  const [selectedCenterId, setSelectedCenterId] = useState(centers[0] ? centers[0].id.toString() : 'ALL');
  
  // Computed multi-line assigned scope for staff / delivery boy
  const userAssignedLineIds = useMemo(() => {
    if (currentUser?.role === 'ADMIN') return null; // Admin has full access to all routes
    if (currentUser?.assigned_line_ids) {
      try {
        const parsed = typeof currentUser.assigned_line_ids === 'string'
          ? JSON.parse(currentUser.assigned_line_ids)
          : currentUser.assigned_line_ids;
        if (Array.isArray(parsed) && parsed.length > 0) {
          const clean = parsed.map(x => parseInt(x)).filter(x => !isNaN(x) && x > 0);
          if (clean.length > 0) return clean;
        }
      } catch {}
    }
    if (currentUser?.assigned_line_id) {
      const pid = parseInt(currentUser.assigned_line_id);
      if (!isNaN(pid) && pid > 0) return [pid];
    }
    return null;
  }, [currentUser]);

  // Auto-preselect assigned line
  const defaultLineId = useMemo(() => {
    if (userAssignedLineIds && userAssignedLineIds.length === 1) {
      return userAssignedLineIds[0].toString();
    }
    return 'ALL';
  }, [userAssignedLineIds]);

  const [selectedLineId, setSelectedLineId] = useState(defaultLineId);
  const [searchQuery, setSearchQuery] = useState('');
  const [extraModalCust, setExtraModalCust] = useState(null);
  const [extraItemQtys, setExtraItemQtys] = useState({}); // { [prodId]: qty }
  const [extraCowDelta, setExtraCowDelta] = useState(0);
  const [extraBufDelta, setExtraBufDelta] = useState(0);

  // Back-Date CRUD permission guard
  const nowD = new Date();
  const todayStr = `${nowD.getFullYear()}-${String(nowD.getMonth() + 1).padStart(2, '0')}-${String(nowD.getDate()).padStart(2, '0')}`;
  const isPastDate = selectedDate < todayStr;
  const isRestrictedRole = currentUser?.role === 'DELIVERY' || currentUser?.role === 'CASHIER';
  const canBackdate = !isRestrictedRole || Boolean(currentUser?.can_backdate_crud);
  const isDateLocked = isPastDate && !canBackdate;
  const [deliveryMap, setDeliveryMap] = useState({});
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('ALL'); // 'ALL' | 'PENDING' | 'DELIVERED' | 'ABSENT'

  // Live Customer Dues mapping: { [customerId]: pendingDue }
  const [customerDues, setCustomerDues] = useState({});
  const [loadingDues, setLoadingDues] = useState(false);
  // Custom Dynamic Milk Rates per customer: { [customerId]: { cowRate, bufRate } }
  const [customRates, setCustomRates] = useState({});

  // Track customers that were already marked 'Delivered' in DB when runsheet loaded
  const initialDbDeliveredRef = useRef(new Set());
  const lastDateShiftRef = useRef('');

  useEffect(() => {
    const key = `${selectedDate}_${shift}`;
    if (lastDateShiftRef.current !== key) {
      lastDateShiftRef.current = key;
      const set = new Set();
      if (Array.isArray(deliveries)) {
        deliveries.forEach(d => {
          if (d.status === 'Delivered') {
            set.add(d.customer_id || d.customerId);
          }
        });
      }
      initialDbDeliveredRef.current = set;
    }
  }, [selectedDate, shift, deliveries]);

  // Quick Payment Modal State
  // Live calculated balances from DB: { [customerId]: balance }
  const [liveBalances, setLiveBalances] = useState({});
  const [checkingBalId, setCheckingBalId] = useState(null);

  const checkLiveCustomerBalance = async (customerId, targetDate) => {
    if (!customerId) return 0;
    const dt = targetDate || selectedDate || todayStr;
    setCheckingBalId(customerId);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customers/${customerId}/calculate-balance?as_of_date=${dt}`);
      const data = await res.json();
      if (data.success && data.current_balance !== undefined) {
        const bal = Math.round(parseFloat(data.current_balance) * 100) / 100;
        setLiveBalances(prev => ({ ...prev, [customerId]: bal }));
        setCustomerDues(prev => ({ ...prev, [customerId]: bal }));
        return bal;
      }
    } catch (err) {
      console.error('Error fetching live customer balance:', err);
    } finally {
      setCheckingBalId(null);
    }
    return customerDues[customerId] || 0;
  };

  const openPaymentModalForCustomer = (cust) => {
    setPaymentModalCust(cust);
    setPaymentCash('');
    setPaymentUpi('');
    setPaymentNotes('');
    const curDate = selectedDate || todayStr;
    setPaymentDate(curDate);
    setPaymentSuccessData(null);
    checkLiveCustomerBalance(cust.id, curDate);
  };

  const [paymentModalCust, setPaymentModalCust] = useState(null);
  const [paymentCash, setPaymentCash] = useState('');
  const [paymentUpi, setPaymentUpi] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(selectedDate || todayStr);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState(null);
  const [paymentToast, setPaymentToast] = useState(null);

  // Dynamic calculations for payment modal
  const paymentCashNum = parseFloat(paymentCash) || 0;
  const paymentUpiNum = parseFloat(paymentUpi) || 0;
  const paymentTotalReceived = Math.round((paymentCashNum + paymentUpiNum) * 100) / 100;
  const paymentCustDue = paymentModalCust
    ? (liveBalances[paymentModalCust.id] !== undefined
        ? Math.round(liveBalances[paymentModalCust.id])
        : (customerDues[paymentModalCust.id] !== undefined
            ? Math.round(customerDues[paymentModalCust.id])
            : (paymentModalCust.opening_balance || paymentModalCust.balance || 0)))
    : 0;
  const paymentRemainingBalance = Math.max(0, Math.round(paymentCustDue - paymentTotalReceived));

  // Build Payment Receipt Message (SMS / WhatsApp)
  const buildReceiptMessage = (isWa = true) => {
    if (!paymentModalCust) return '';
    const dairy = currentTenant?.brand_name_mr || currentTenant?.name || 'डेअरी';
    const custName = paymentModalCust.name_mr || paymentModalCust.name || 'ग्राहक';
    const [y, m, d] = (paymentDate || todayStr).split('-');
    const receiptDate = (y && m && d) ? `${d}/${m}/${y}` : (paymentDate || todayStr);

    if (isWa) {
      let msg = `*${dairy}*\n`;
      msg += `*पेमेंट पावती (Payment Receipt)*\n`;
      msg += `--------------------------------\n`;
      msg += `दिनांक: ${receiptDate}\n`;
      msg += `ग्राहक: *${custName}*\n`;
      if (paymentCashNum > 0) msg += `रोख जमा (Cash): *₹${paymentCashNum.toFixed(2)}*\n`;
      if (paymentUpiNum > 0) msg += `UPI जमा: *₹${paymentUpiNum.toFixed(2)}*\n`;
      msg += `मिळालेली एकूण रक्कम: *₹${paymentTotalReceived.toFixed(2)}*\n`;
      msg += `--------------------------------\n`;
      msg += `आज अखेर उर्वरित बाकी शिल्लक: *₹${paymentRemainingBalance}*\n`;
      if (paymentNotes) msg += `नोंद: ${paymentNotes}\n`;
      msg += `\nपावती नोंदवून रक्कम स्वीकारली आहे. धन्यवाद! 🙏`;
      return msg;
    } else {
      let msg = `${dairy} - पेमेंट पावती\n`;
      msg += `दि: ${receiptDate}\n`;
      msg += `ग्राहक: ${custName}\n`;
      if (paymentCashNum > 0) msg += `रोख: ₹${paymentCashNum.toFixed(2)}\n`;
      if (paymentUpiNum > 0) msg += `UPI: ₹${paymentUpiNum.toFixed(2)}\n`;
      msg += `एकूण जमा: ₹${paymentTotalReceived.toFixed(2)}\n`;
      msg += `उर्वरित बाकी: ₹${paymentRemainingBalance}\n`;
      msg += `धन्यवाद!`;
      return msg;
    }
  };

  const handleSendWhatsAppReceipt = () => {
    if (!paymentModalCust) return;
    const cleanPhone = (paymentModalCust.phone || '').replace(/\D/g, '');
    const text = buildReceiptMessage(true);
    const url = cleanPhone ? `https://wa.me/91${cleanPhone}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const handleSendSmsReceipt = () => {
    if (!paymentModalCust) return;
    const cleanPhone = (paymentModalCust.phone || '').replace(/\D/g, '');
    const text = buildReceiptMessage(false);
    const url = cleanPhone ? `sms:${cleanPhone}?body=${encodeURIComponent(text)}` : `sms:?body=${encodeURIComponent(text)}`;
    window.location.href = url;
  }; // { type, text, whatsappUrl }

  // Permission guards for runsheet operations
  const canModifyRunsheet = Boolean(
    currentUser?.role === 'ADMIN' ||
    currentUser?.permissions?.runsheet?.modify === true ||
    (currentUser?.role === 'DELIVERY' && currentUser?.permissions?.runsheet?.modify !== false && !currentUser?.permissions)
  );

  const canModifyRate = Boolean(
    currentUser?.role === 'ADMIN' ||
    currentUser?.permissions?.runsheet?.modify_rate === true ||
    currentUser?.can_modify_rate === true
  );

  const canAcceptPayments = Boolean(
    currentUser?.role === 'ADMIN' ||
    currentUser?.role === 'CASHIER' ||
    currentUser?.permissions?.payments?.add === true ||
    currentUser?.permissions?.billing?.add === true ||
    currentUser?.permissions?.billing?.modify === true ||
    currentUser?.permissions?.runsheet?.collect_payment === true ||
    currentUser?.can_accept_payments === true
  );

  // Fetch monthly summary to get live pending dues for each customer as of selectedDate
  const fetchCustomerDues = useCallback(async () => {
    if (!tenantSlug) return;
    const targetYm = (selectedDate || todayStr).substring(0, 7);
    const targetDate = selectedDate || todayStr;
    setLoadingDues(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/billing/monthly-summary?year_month=${targetYm}&as_of_date=${targetDate}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.billing_records)) {
        const dues = {};
        data.billing_records.forEach(r => {
          dues[r.customerId] = parseFloat(r.pendingAmount || 0);
        });
        setCustomerDues(dues);
      }
    } catch (err) {
      console.warn('Could not load live customer dues:', err);
    } finally {
      setLoadingDues(false);
    }
  }, [tenantSlug, selectedDate, todayStr]);

  // When selectedDate changes, reset liveBalances so cached balance from another date doesn't persist
  useEffect(() => {
    setLiveBalances({});
  }, [selectedDate]);

  useEffect(() => {
    fetchCustomerDues();
  }, [fetchCustomerDues]);

  // Handle Quick Payment Submission
  const handleRecordPayment = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!paymentModalCust) return;
    const custId = paymentModalCust.id;

    if (paymentTotalReceived <= 0) {
      alert(isMr ? 'कृपया रोख किंवा UPI रक्कम प्रविष्ट करा.' : 'Please enter Cash or UPI amount.');
      return;
    }

    setSubmittingPayment(true);
    try {
      const mode = (paymentCashNum > 0 && paymentUpiNum > 0) ? 'SPLIT' : (paymentUpiNum > 0 ? 'UPI' : 'CASH');
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customer-payments`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          customer_id: custId,
          payment_date: paymentDate || todayStr,
          payment_mode: mode,
          cash_amount: paymentCashNum,
          upi_amount: paymentUpiNum,
          cheque_amount: 0,
          total_amount: paymentTotalReceived,
          dues_at_payment: paymentCustDue,
          reference_no: paymentNotes ? paymentNotes.slice(0, 50) : '',
          remarks: paymentNotes || 'वाटप दरम्यान जमा (Collected during delivery)',
          notes: paymentNotes || 'वाटप दरम्यान जमा (Collected during delivery)'
        })
      });

      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        const textErr = await res.text().catch(() => '');
        throw new Error(`सर्व्हर त्रुटी (${res.status}): ${textErr.slice(0, 150) || res.statusText}`);
      }

      if (data && data.success) {
        // Immediate local balance update
        const updatedDue = Math.max(0, Math.round(paymentCustDue - paymentTotalReceived));
        setCustomerDues(prev => ({
          ...prev,
          [custId]: updatedDue
        }));

        const cleanCustPhone = (paymentModalCust.phone || '').replace(/\D/g, '');
        const custName = paymentModalCust.name_mr || paymentModalCust.name || 'ग्राहक';
        const waText = buildReceiptMessage(true);
        const waLink = cleanCustPhone ? `https://wa.me/91${cleanCustPhone}?text=${encodeURIComponent(waText)}` : '';

        setPaymentToast({
          type: 'success',
          text: isMr 
            ? `${custName} यांच्या खाती ₹${paymentTotalReceived} जमा झाले! उर्वरित बाकी: ₹${updatedDue}` 
            : `₹${paymentTotalReceived} recorded for ${custName}! Remaining: ₹${updatedDue}`,
          whatsappUrl: waLink
        });

        // Show completed confirmation inside modal
        setPaymentSuccessData({
          cust: paymentModalCust,
          totalAmt: paymentTotalReceived,
          updatedDue,
          cleanPhone: cleanCustPhone,
          waLink
        });
      } else {
        alert(data?.message || 'पेमेंट नोंदवता आले नाही.');
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert('सर्व्हर एरर: ' + (err.message || 'काहीतरी त्रुटी झाली'));
    } finally {
      setSubmittingPayment(false);
    }
  };

  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteLineId, setBulkDeleteLineId] = useState('ALL');
  const [bulkDeleteShift, setBulkDeleteShift] = useState('ALL');
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const canDeleteRunsheet = !isDateLocked && (currentUser?.role === 'ADMIN' || Boolean(currentUser?.permissions?.runsheet?.delete));

  // Sync deliveryMap from deliveries prop or array
  useEffect(() => {
    const map = {};
    if (Array.isArray(deliveries)) {
      deliveries.forEach(d => {
        const cCow = d.cowQty !== undefined ? d.cowQty : (d.cow_qty !== undefined ? d.cow_qty : 0);
        const cBuf = d.bufQty !== undefined ? d.bufQty : (d.buf_qty !== undefined ? d.buf_qty : 0);
        map[d.customer_id || d.customerId] = {
          status: d.status,
          time: d.delivery_time || d.time,
          cowQty: parseFloat(cCow),
          cow_qty: parseFloat(cCow),
          bufQty: parseFloat(cBuf),
          buf_qty: parseFloat(cBuf),
          extraDairy: d.extra_dairy_items ? (typeof d.extra_dairy_items === 'string' ? JSON.parse(d.extra_dairy_items) : d.extra_dairy_items) : (d.extraDairy || [])
        };
      });
    } else if (typeof deliveries === 'object' && deliveries !== null) {
      setDeliveryMap(deliveries);
      return;
    }
    setDeliveryMap(map);
  }, [deliveries]);

  // Update selected center if empty
  useEffect(() => {
    if (centers.length > 0 && selectedCenterId === 'ALL') {
      setSelectedCenterId(centers[0].id.toString());
    }
  }, [centers, selectedCenterId]);

  // Filter lines by selected center
  const centerLines = useMemo(() => {
    return lines.filter(l => {
      if (selectedCenterId === 'ALL') return true;
      const cId = (l.center_id || l.centerId || '').toString();
      return cId === selectedCenterId.toString();
    });
  }, [lines, selectedCenterId]);

  // Filter accessible lines based on assigned multi-routes
  const accessibleLines = useMemo(() => {
    if (!userAssignedLineIds) return centerLines;
    return centerLines.filter(l => userAssignedLineIds.includes(parseInt(l.id)));
  }, [centerLines, userAssignedLineIds]);

  // Filter customers by assigned routes + selected line + search query
  const filteredCustomers = useMemo(() => {
    return customers.filter(c => {
      const cLineId = parseInt(c.line_id || c.lineId || 0);

      // Enforce assigned multi-route access
      if (userAssignedLineIds && !userAssignedLineIds.includes(cLineId)) {
        return false;
      }

      const matchesLine = selectedLineId === 'ALL' || cLineId.toString() === selectedLineId.toString();
      const nameStr = (c.name_mr || c.name || '').toLowerCase();
      const phoneStr = (c.phone || '').toLowerCase();
      const addrStr = (c.address || '').toLowerCase();
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || nameStr.includes(q) || phoneStr.includes(q) || addrStr.includes(q);
      return matchesLine && matchesSearch;
    });
  }, [customers, selectedLineId, searchQuery, userAssignedLineIds]);

  // Filtered customers based on deliveryStatusFilter (ALL / PENDING / DELIVERED / ABSENT)
  const displayedCustomers = useMemo(() => {
    return filteredCustomers.filter(c => {
      const d = deliveryMap[c.id];
      const isDel = d?.status === 'Delivered';
      const isAbs = d?.status === 'Absent' || c.status === 'Vacation';
      const isPend = !isDel && !isAbs;

      if (deliveryStatusFilter === 'PENDING') return isPend;
      if (deliveryStatusFilter === 'DELIVERED') return isDel;
      if (deliveryStatusFilter === 'ABSENT') return isAbs;
      return true;
    });
  }, [filteredCustomers, deliveryMap, deliveryStatusFilter]);

  // Dynamic extra products based on enabled feature flags (strictly non-milk)
  const extraAvailableProducts = useMemo(() => {
    return products.filter(p => {
      const cat = (p.category || '').toLowerCase();
      if (cat === 'milk') return false;
      if (isDairyEnabled && (cat === 'dairy' || cat === 'byproduct' || cat === 'dairy_byproducts')) return true;
      if (isBakeryEnabled && (cat === 'bakery' || cat === 'grocery' || cat === 'bakery_products')) return true;
      return Boolean(isDairyEnabled || isBakeryEnabled);
    });
  }, [products, isDairyEnabled, isBakeryEnabled]);

  // Save single delivery update to backend API (100% Live DB Sync)
  const persistDelivery = async (custId, updatedData) => {
    const cust = customers.find(c => c.id === custId);
    if (!cust) return;

    const token = localStorage.getItem('milkpro_tenant_token');
    const sessionToken = localStorage.getItem('milkpro_session_token');

    const payload = {
      user_id: currentUser?.id,
      session_token: sessionToken,
      deliveries: [
        {
          delivery_date: selectedDate,
          shift,
          customer_id: custId,
          line_id: cust.line_id || cust.lineId || 1,
          status: updatedData.status || 'Delivered',
          cow_qty: updatedData.cowQty !== undefined ? updatedData.cowQty : 0,
          buf_qty: updatedData.bufQty !== undefined ? updatedData.bufQty : 0,
          cow_rate: updatedData.cowRate !== undefined ? updatedData.cowRate : (customRates[custId]?.cowRate ?? null),
          buf_rate: updatedData.bufRate !== undefined ? updatedData.bufRate : (customRates[custId]?.bufRate ?? null),
          extra_dairy_items: updatedData.extraDairy || [],
          delivery_time: updatedData.time || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]
    };

    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/deliveries`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-Session-Token': sessionToken || ''
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data && data.session_terminated) {
        if (onLogout) {
          onLogout(data.message);
        }
      }
    } catch (err) {
      console.error('Failed to persist delivery:', err);
    }
  };

  // Toggle Delivered status
  const toggleDelivered = (cust) => {
    if (isDateLocked) {
      alert(isMr ? 'मागील तारखेच्या नोंदी बदलण्याची परवानगी नाही.' : 'Back-date modifications not permitted.');
      return;
    }
    if (!canModifyRunsheet) {
      alert(isMr ? 'आपल्याला वाटप नोंदी बदलण्याची परवानगी नाही.' : 'You do not have permission to modify delivery records.');
      return;
    }
    const curr = deliveryMap[cust.id] || {};
    const newStatus = curr.status === 'Delivered' ? 'Pending' : 'Delivered';
    const timeNow = newStatus === 'Delivered' ? new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
    
    const cowQuota = shift === 'Morning' 
      ? parseFloat(cust.morning_cow_qty !== undefined ? cust.morning_cow_qty : (cust.morningCow || 0))
      : parseFloat(cust.evening_cow_qty !== undefined ? cust.evening_cow_qty : (cust.eveningCow || 0));
    
    const bufQuota = shift === 'Morning' 
      ? parseFloat(cust.morning_buf_qty !== undefined ? cust.morning_buf_qty : (cust.morningBuf || 0))
      : parseFloat(cust.evening_buf_qty !== undefined ? cust.evening_buf_qty : (cust.eveningBuf || 0));

    const tCow = curr.cowQty !== undefined ? curr.cowQty : (curr.cow_qty !== undefined ? curr.cow_qty : cowQuota);
    const tBuf = curr.bufQty !== undefined ? curr.bufQty : (curr.buf_qty !== undefined ? curr.buf_qty : bufQuota);
    const updated = {
      ...curr,
      status: newStatus,
      time: timeNow,
      cowQty: tCow,
      cow_qty: tCow,
      bufQty: tBuf,
      buf_qty: tBuf,
      extraDairy: curr.extraDairy || []
    };

    setDeliveryMap(prev => ({ ...prev, [cust.id]: updated }));
    persistDelivery(cust.id, updated);
    if (onUpdateDelivery) onUpdateDelivery(cust.id, updated);
  };

  // POS-style Qty Stepper (+/- 0.5 L)
  const handleStepQty = (cust, type, delta) => {
    if (isDateLocked) {
      alert(isMr ? 'मागील तारखेच्या नोंदी बदलण्याची परवानगी नाही.' : 'Back-date modifications not permitted.');
      return;
    }
    const curr = deliveryMap[cust.id] || {};
    const cowQuota = shift === 'Morning' 
      ? parseFloat(cust.morning_cow_qty !== undefined ? cust.morning_cow_qty : (cust.morningCow || 0))
      : parseFloat(cust.evening_cow_qty !== undefined ? cust.evening_cow_qty : (cust.eveningCow || 0));
    const bufQuota = shift === 'Morning' 
      ? parseFloat(cust.morning_buf_qty !== undefined ? cust.morning_buf_qty : (cust.morningBuf || 0))
      : parseFloat(cust.evening_buf_qty !== undefined ? cust.evening_buf_qty : (cust.eveningBuf || 0));

    const currentCow = curr.cowQty !== undefined ? curr.cowQty : cowQuota;
    const currentBuf = curr.bufQty !== undefined ? curr.bufQty : bufQuota;

    let newCow = currentCow;
    let newBuf = currentBuf;

    if (type === 'cow') {
      newCow = Math.max(0, Math.round((currentCow + delta) * 100) / 100);
    } else {
      newBuf = Math.max(0, Math.round((currentBuf + delta) * 100) / 100);
    }

    const updated = {
      ...curr,
      status: (newCow > 0 || newBuf > 0) ? 'Delivered' : (curr.status || 'Pending'),
      cowQty: newCow,
      cow_qty: newCow,
      bufQty: newBuf,
      buf_qty: newBuf,
      extraDairy: curr.extraDairy || []
    };

    setDeliveryMap(prev => ({ ...prev, [cust.id]: updated }));
    persistDelivery(cust.id, updated);
    if (onUpdateDelivery) onUpdateDelivery(cust.id, updated);
  };

  // Direct Qty Input (allows typing odd decimals like 0.2, 0.75, 1.25, etc.)
  // Bulk Delete Deliveries (Line, Shift, Date)
  const handleBulkDeleteDeliveries = async () => {
    const lineObj = lines.find(l => String(l.id) === String(bulkDeleteLineId));
    const lineName = bulkDeleteLineId === 'ALL' ? (isMr ? 'सर्व मार्ग' : 'All Lines') : (lineObj?.name_mr || lineObj?.name || 'Line');
    const shiftName = bulkDeleteShift === 'ALL' ? (isMr ? 'सकाळ व संध्याकाळ' : 'All Shifts') : (bulkDeleteShift === 'Morning' ? (isMr ? 'सकाळ' : 'Morning') : (isMr ? 'संध्याकाळ' : 'Evening'));

    const confirmMsg = isMr
      ? `⚠️ अतिशय महत्त्वाचे (Final Confirmation):\n\nदिनांक: ${selectedDate}\nमार्ग: ${lineName}\nशिफ्ट: ${shiftName}\n\nया निवडीतील सर्व वाटप नोंदी कायमच्या डिलीट / रीसेट करायच्या आहेत का?\nयामुळे सर्व ग्राहकांचे स्टेटस पुन्हा 'Pending' होईल.`
      : `Are you sure you want to permanently DELETE deliveries for Date: ${selectedDate}, Route: ${lineName}, Shift: ${shiftName}?`;

    if (!window.confirm(confirmMsg)) return;

    setIsDeletingBulk(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/deliveries/bulk-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('milkpro_tenant_token') ? `Bearer ${localStorage.getItem('milkpro_tenant_token')}` : '',
          'X-Session-Token': localStorage.getItem('milkpro_session_token') || ''
        },
        body: JSON.stringify({
          date: selectedDate,
          shift: bulkDeleteShift,
          line_id: bulkDeleteLineId,
          center_id: selectedCenterId
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message || 'नोंदी यशस्वीरित्या हटवण्यात आल्या.');
        setIsBulkDeleteModalOpen(false);
        // Reset local delivery map for deleted items
        setDeliveryMap(prev => {
          const next = { ...prev };
          filteredCustomers.forEach(c => {
            const matchesLine = bulkDeleteLineId === 'ALL' || String(c.line_id || c.lineId) === String(bulkDeleteLineId);
            if (matchesLine) {
              delete next[c.id];
            }
          });
          return next;
        });
        if (onUpdateDelivery) onUpdateDelivery(null, null);
      } else {
        alert(data.message || 'नोंदी हटवण्यात त्रुटी आली.');
      }
    } catch (err) {
      alert('सर्व्हरशी संपर्क होऊ शकला नाही: ' + err.message);
    } finally {
      setIsDeletingBulk(false);
    }
  };

  const handleDirectQtyChange = (cust, type, valStr) => {
    if (isDateLocked) {
      alert(isMr ? 'मागील तारखेच्या नोंदी बदलण्याची परवानगी नाही.' : 'Back-date modifications not permitted.');
      return;
    }
    const val = valStr === '' ? 0 : parseFloat(valStr);
    const parsedVal = isNaN(val) ? 0 : Math.max(0, Math.round(val * 100) / 100);

    const curr = deliveryMap[cust.id] || {};
    const cowQuota = shift === 'Morning' 
      ? parseFloat(cust.morning_cow_qty !== undefined ? cust.morning_cow_qty : (cust.morningCow || 0))
      : parseFloat(cust.evening_cow_qty !== undefined ? cust.evening_cow_qty : (cust.eveningCow || 0));
    const bufQuota = shift === 'Morning' 
      ? parseFloat(cust.morning_buf_qty !== undefined ? cust.morning_buf_qty : (cust.morningBuf || 0))
      : parseFloat(cust.evening_buf_qty !== undefined ? cust.evening_buf_qty : (cust.eveningBuf || 0));

    const newCowVal = type === 'cow' ? parsedVal : (curr.cowQty !== undefined ? curr.cowQty : (curr.cow_qty !== undefined ? curr.cow_qty : cowQuota));
    const newBufVal = type === 'buf' ? parsedVal : (curr.bufQty !== undefined ? curr.bufQty : (curr.buf_qty !== undefined ? curr.buf_qty : bufQuota));
    const updated = {
      ...curr,
      status: (newCowVal > 0 || newBufVal > 0) ? 'Delivered' : (curr.status || 'Pending'),
      cowQty: newCowVal,
      cow_qty: newCowVal,
      bufQty: newBufVal,
      buf_qty: newBufVal,
      extraDairy: curr.extraDairy || []
    };

    setDeliveryMap(prev => ({ ...prev, [cust.id]: updated }));
    persistDelivery(cust.id, updated);
    if (onUpdateDelivery) onUpdateDelivery(cust.id, updated);
  };

  // Mark customer as Absent (खाडा)
  const markAbsent = (cust) => {
    if (isDateLocked) {
      alert(isMr ? 'मागील तारखेच्या नोंदी बदलण्याची परवानगी नाही.' : 'Back-date modifications not permitted.');
      return;
    }
    if (!canModifyRunsheet) {
      alert(isMr ? 'आपल्याला वाटप नोंदी बदलण्याची परवानगी नाही.' : 'You do not have permission to modify delivery records.');
      return;
    }
    const curr = deliveryMap[cust.id] || {};
    const newStatus = curr.status === 'Absent' ? 'Pending' : 'Absent';
    
    const cowQuota = shift === 'Morning' 
      ? parseFloat(cust.morning_cow_qty !== undefined ? cust.morning_cow_qty : (cust.morningCow || 0))
      : parseFloat(cust.evening_cow_qty !== undefined ? cust.evening_cow_qty : (cust.eveningCow || 0));
    
    const bufQuota = shift === 'Morning' 
      ? parseFloat(cust.morning_buf_qty !== undefined ? cust.morning_buf_qty : (cust.morningBuf || 0))
      : parseFloat(cust.evening_buf_qty !== undefined ? cust.evening_buf_qty : (cust.eveningBuf || 0));

    const updated = {
      ...curr,
      status: newStatus,
      time: null,
      cowQty: newStatus === 'Absent' ? 0 : cowQuota,
      bufQty: newStatus === 'Absent' ? 0 : bufQuota,
      extraDairy: newStatus === 'Absent' ? [] : (curr.extraDairy || [])
    };

    setDeliveryMap(prev => ({ ...prev, [cust.id]: updated }));
    persistDelivery(cust.id, updated);
    if (onUpdateDelivery) onUpdateDelivery(cust.id, updated);
  };

  // Open Extra modal
  const openExtraModal = (cust) => {
    if (isDateLocked) {
      alert(isMr ? 'मागील तारखेच्या नोंदी बदलण्याची परवानगी नाही.' : 'Back-date modifications not permitted.');
      return;
    }
    setExtraModalCust(cust);
    const existing = deliveryMap[cust.id]?.extraDairy || [];
    const qMap = {};
    existing.forEach(item => {
      qMap[item.prodId] = item.qty || 1;
    });
    setExtraItemQtys(qMap);
    setExtraCowDelta(0);
    setExtraBufDelta(0);
  };

  // Adjust item qty in modal
  const setItemQty = (prodId, val) => {
    const num = Math.max(0, parseFloat(val) || 0);
    setExtraItemQtys(prev => ({ ...prev, [prodId]: num }));
  };

  // Save all extra products from modal
  const handleSaveExtraModal = () => {
    if (!extraModalCust) return;
    const curr = deliveryMap[extraModalCust.id] || {};
    const list = [];

    extraAvailableProducts.forEach(prod => {
      const q = extraItemQtys[prod.id] || 0;
      if (q > 0) {
        list.push({
          prodId: prod.id,
          name: isMr ? (prod.name_mr || prod.name) : (prod.name_en || prod.name),
          qty: q,
          rate: parseFloat(prod.retail_rate !== undefined ? prod.retail_rate : (prod.retailRate || 0))
        });
      }
    });

    const cowBase = curr.cowQty !== undefined ? curr.cowQty : (
      shift === 'Morning' 
        ? parseFloat(extraModalCust.morning_cow_qty !== undefined ? extraModalCust.morning_cow_qty : (extraModalCust.morningCow || 0))
        : parseFloat(extraModalCust.evening_cow_qty !== undefined ? extraModalCust.evening_cow_qty : (extraModalCust.eveningCow || 0))
    );
    const bufBase = curr.bufQty !== undefined ? curr.bufQty : (
      shift === 'Morning' 
        ? parseFloat(extraModalCust.morning_buf_qty !== undefined ? extraModalCust.morning_buf_qty : (extraModalCust.morningBuf || 0))
        : parseFloat(extraModalCust.evening_buf_qty !== undefined ? extraModalCust.evening_buf_qty : (extraModalCust.eveningBuf || 0))
    );

    const updated = {
      ...curr,
      cowQty: Math.max(0, cowBase + extraCowDelta),
      bufQty: Math.max(0, bufBase + extraBufDelta),
      extraDairy: list
    };

    setDeliveryMap(prev => ({ ...prev, [extraModalCust.id]: updated }));
    persistDelivery(extraModalCust.id, updated);
    if (onUpdateDelivery) onUpdateDelivery(extraModalCust.id, updated);
    setExtraModalCust(null);
  };

  // Live Metrics Calculations
  const deliveredCount = filteredCustomers.filter(c => deliveryMap[c.id]?.status === 'Delivered').length;
  const absentCount = filteredCustomers.filter(c => deliveryMap[c.id]?.status === 'Absent' || c.status === 'Vacation').length;
  const pendingCount = filteredCustomers.length - deliveredCount - absentCount;
  const progressPercent = filteredCustomers.length > 0 
    ? Math.round((deliveredCount / filteredCustomers.length) * 100) 
    : 0;

  // Total Liters
  const totalCowDelivered = filteredCustomers.reduce((sum, c) => {
    const d = deliveryMap[c.id];
    if (d?.status === 'Delivered') return sum + (d.cowQty || 0);
    return sum;
  }, 0);

  const totalBufDelivered = filteredCustomers.reduce((sum, c) => {
    const d = deliveryMap[c.id];
    if (d?.status === 'Delivered') return sum + (d.bufQty || 0);
    return sum;
  }, 0);

  // Total Byproduct Revenue
  const totalByproductRevenue = filteredCustomers.reduce((sum, c) => {
    const d = deliveryMap[c.id];
    if (d?.extraDairy && Array.isArray(d.extraDairy)) {
      return sum + d.extraDairy.reduce((sub, it) => sub + ((it.qty || 1) * (it.rate || 0)), 0);
    }
    return sum;
  }, 0);

  // Customers with extra orders today
  const customersWithExtraOrders = useMemo(() => {
    return filteredCustomers.filter(c => {
      const d = deliveryMap[c.id];
      return d?.extraDairy && d.extraDairy.length > 0;
    });
  }, [filteredCustomers, deliveryMap]);

  return (
    <div style={{
      background: '#e2e8f0', // स्पष्ट कॉन्ट्रास्ट स्लेट बॅकग्राउंड
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      paddingBottom: isDeliveryRole ? '75px' : '20px'
    }}>
      
      {/* Back-Date Lock Warning Banner */}
      {isDateLocked && (
        <div style={{ background: '#fef2f2', borderBottom: '1.5px solid #fecdd3', color: '#b91c1c', padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', fontWeight: 800 }}>
          <AlertCircle size={16} />
          <span>
            {isMr 
              ? `मागील तारखेच्या (${selectedDate}) नोंदी पाहण्यास परवानगी आहे, बदल मर्यादित आहेत.`
              : `Back-date entries (${selectedDate}) are view-only.`}
          </span>
        </div>
      )}

      {/* SUB-HEADER: Route Live Progress Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
        color: '#ffffff',
        padding: '10px 14px',
        boxShadow: '0 2px 8px rgba(2, 132, 199, 0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.76rem', fontWeight: 800, marginBottom: 4 }}>
          <span>वाटप प्रगती: {deliveredCount}/{filteredCustomers.length} ग्राहक पूर्ण</span>
          <span style={{ background: 'rgba(255,255,255,0.2)', padding: '1px 8px', borderRadius: 12 }}>{progressPercent}% पूर्ण</span>
        </div>
        <div style={{ width: '100%', height: 7, background: 'rgba(255,255,255,0.25)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ width: `${progressPercent}%`, height: '100%', background: '#4ade80', borderRadius: 4, transition: 'width 0.3s ease' }} />
        </div>
      </div>

      {/* FILTER & SEARCH BAR */}
      <div style={{
        background: '#ffffff',
        padding: '8px 12px',
        borderBottom: '1.5px solid #cbd5e1',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 10, top: 10 }} />
          <input
            type="text"
            placeholder={isMr ? "🔍 ग्राहक किंवा पत्ता शोधा..." : "Search customer..."}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '6px 10px 6px 30px',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: '0.82rem',
              outline: 'none',
              background: '#f8fafc',
              fontWeight: 600
            }}
          />
        </div>

        <select
          value={selectedLineId}
          onChange={e => setSelectedLineId(e.target.value)}
          style={{
            padding: '6px 8px',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            fontSize: '0.78rem',
            fontWeight: 800,
            color: '#1e293b',
            background: '#ffffff',
            outline: 'none'
          }}
        >
          <option value="ALL">{isMr ? 'सर्व मार्ग (All Lines)' : 'All Lines'}</option>
          {accessibleLines.map(l => (
            <option key={l.id} value={l.id.toString()}>{l.name_mr || l.name}</option>
          ))}
        </select>
      </div>

      {/* Quick Payment Toast Alert */}
      {paymentToast && (
        <div style={{
          background: '#dcfce7',
          border: '1.5px solid #86efac',
          borderRadius: 12,
          padding: '10px 14px',
          margin: '8px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          boxShadow: '0 2px 8px rgba(22, 163, 74, 0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', fontWeight: 800, color: '#15803d' }}>
            <span>✅</span>
            <span>{paymentToast.text}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {paymentToast.whatsappUrl && (
              <a
                href={paymentToast.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  background: '#16a34a',
                  color: '#ffffff',
                  padding: '5px 12px',
                  borderRadius: 6,
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <span>🟢</span>
                <span>{isMr ? 'पावती WhatsApp पाठवा' : 'Send WhatsApp Receipt'}</span>
              </a>
            )}
            <button
              type="button"
              onClick={() => setPaymentToast(null)}
              style={{ background: 'transparent', border: 'none', color: '#15803d', fontWeight: 900, cursor: 'pointer', fontSize: '1.1rem' }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* QUICK STATUS FILTERS & BULK ACTIONS BAR */}
      <div style={{
        background: '#ffffff',
        padding: '6px 12px',
        borderBottom: '1.5px solid #cbd5e1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 6
      }}>
        {/* Quick Filter Pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setDeliveryStatusFilter('ALL')}
            style={{
              padding: '4px 10px',
              borderRadius: 20,
              fontSize: '0.74rem',
              fontWeight: 800,
              border: deliveryStatusFilter === 'ALL' ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
              background: deliveryStatusFilter === 'ALL' ? '#0284c7' : '#ffffff',
              color: deliveryStatusFilter === 'ALL' ? '#ffffff' : '#334155',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
          >
            <span>सर्व</span>
            <span style={{ opacity: 0.85 }}>({filteredCustomers.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setDeliveryStatusFilter('PENDING')}
            style={{
              padding: '4px 10px',
              borderRadius: 20,
              fontSize: '0.74rem',
              fontWeight: 800,
              border: deliveryStatusFilter === 'PENDING' ? '1.5px solid #d97706' : '1px solid #fde68a',
              background: deliveryStatusFilter === 'PENDING' ? '#d97706' : '#fffbeb',
              color: deliveryStatusFilter === 'PENDING' ? '#ffffff' : '#b45309',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              boxShadow: deliveryStatusFilter === 'PENDING' ? '0 2px 6px rgba(217, 119, 6, 0.25)' : 'none'
            }}
            title={isMr ? "केवळ ज्यांचे वाटप बाकी आहे तेवढेच दाखवा" : "Show Pending only"}
          >
            <span>⏳ बाकी</span>
            <span style={{ fontWeight: 900 }}>({pendingCount})</span>
          </button>

          <button
            type="button"
            onClick={() => setDeliveryStatusFilter('DELIVERED')}
            style={{
              padding: '4px 10px',
              borderRadius: 20,
              fontSize: '0.74rem',
              fontWeight: 800,
              border: deliveryStatusFilter === 'DELIVERED' ? '1.5px solid #16a34a' : '1px solid #bbf7d0',
              background: deliveryStatusFilter === 'DELIVERED' ? '#16a34a' : '#f0fdf4',
              color: deliveryStatusFilter === 'DELIVERED' ? '#ffffff' : '#15803d',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
          >
            <span>✅ पूर्ण</span>
            <span>({deliveredCount})</span>
          </button>

          <button
            type="button"
            onClick={() => setDeliveryStatusFilter('ABSENT')}
            style={{
              padding: '4px 10px',
              borderRadius: 20,
              fontSize: '0.74rem',
              fontWeight: 800,
              border: deliveryStatusFilter === 'ABSENT' ? '1.5px solid #dc2626' : '1px solid #fecaca',
              background: deliveryStatusFilter === 'ABSENT' ? '#dc2626' : '#fef2f2',
              color: deliveryStatusFilter === 'ABSENT' ? '#ffffff' : '#b91c1c',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}
          >
            <span>❌ खाडे</span>
            <span>({absentCount})</span>
          </button>
        </div>

        {/* Bulk Delete / Reset Button */}
        {canDeleteRunsheet && (
          <button
            type="button"
            onClick={() => setIsBulkDeleteModalOpen(true)}
            style={{
              padding: '4px 9px',
              borderRadius: 8,
              border: '1px solid #fca5a5',
              background: '#fff1f2',
              color: '#b91c1c',
              fontSize: '0.73rem',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 5
            }}
            title={isMr ? "या दिवसाच्या/मार्गाच्या वाटप नोंदी रीसेट किंवा डिलीट करा" : "Reset or Delete Deliveries"}
          >
            <span>🗑️ वाटप रीसेट</span>
          </button>
        )}
      </div>

      {/* ============================================================
          TAB 1: RUNSHEET (ग्राहक वितरण यादी - सुटसुटीत स्ट्रिप्स)
          ============================================================ */}
      {deliveryTab === 'runsheet' && (
        <div style={{
          padding: '12px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}>
          {filteredCustomers.length === 0 ? (
            <div style={{ background: '#ffffff', borderRadius: 14, padding: '2.5rem 1rem', textAlign: 'center', color: '#64748b', border: '1.5px solid #cbd5e1' }}>
              <div style={{ fontSize: '1.8rem', marginBottom: 6 }}>🛵</div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>
                {isMr ? 'या मार्गावर कोणतेही ग्राहक उपलब्ध नाहीत.' : 'No customers on this line.'}
              </div>
            </div>
          ) : (
            displayedCustomers.map((c, idx) => {
              const isVacation = c.status === 'Vacation';
              const delInfo = deliveryMap[c.id] || {};
              const isDelivered = delInfo.status === 'Delivered';
              const isAbsent = delInfo.status === 'Absent' || isVacation;
              
              const cowQuota = shift === 'Morning' 
                ? parseFloat(c.morning_cow_qty !== undefined ? c.morning_cow_qty : (c.morningCow || 0))
                : parseFloat(c.evening_cow_qty !== undefined ? c.evening_cow_qty : (c.eveningCow || 0));
              
              const bufQuota = shift === 'Morning' 
                ? parseFloat(c.morning_buf_qty !== undefined ? c.morning_buf_qty : (c.morningBuf || 0))
                : parseFloat(c.evening_buf_qty !== undefined ? c.evening_buf_qty : (c.eveningBuf || 0));

              const curCow = delInfo.cowQty !== undefined ? delInfo.cowQty : cowQuota;
              const curBuf = delInfo.bufQty !== undefined ? delInfo.bufQty : bufQuota;

              // Strict rights check for editing quantities (must have modify rights & not date locked)
              const canEditQty = !isDateLocked && canModifyRunsheet;

              // Base Milk Rates Resolution
              const cowProdItem = (products || []).find(p => {
                const name = (p.name_mr || p.name || '').toLowerCase();
                const cat = (p.category || '').toLowerCase();
                return (cat === 'milk' || cat === 'दूध') && name.includes('गाय');
              }) || (products || []).find(p => (p.name_mr || p.name || '').includes('गाय'));

              const bufProdItem = (products || []).find(p => {
                const name = (p.name_mr || p.name || '').toLowerCase();
                const cat = (p.category || '').toLowerCase();
                return (cat === 'milk' || cat === 'दूध') && name.includes('म्हैस');
              }) || (products || []).find(p => (p.name_mr || p.name || '').includes('म्हैस'));

              const defaultCowRate = cowProdItem ? parseFloat(cowProdItem.retail_rate || cowProdItem.retailRate || 56) : 56;
              const defaultBufRate = bufProdItem ? parseFloat(bufProdItem.retail_rate || bufProdItem.retailRate || 72) : 72;

              const baseCowRate = parseFloat(c.cow_rate || c.custom_cow_rate || defaultCowRate);
              const baseBufRate = parseFloat(c.buf_rate || c.buffalo_rate || c.custom_buf_rate || defaultBufRate);

              // Dynamic Customer Check & Active Rate Resolution
              const isDynamicCustomer = Boolean(c.is_dynamic_rate);
              const activeCowRate = customRates[c.id]?.cowRate !== undefined 
                ? customRates[c.id].cowRate 
                : (delInfo?.cow_rate !== undefined && delInfo?.cow_rate !== null ? parseFloat(delInfo.cow_rate) : baseCowRate);

              const activeBufRate = customRates[c.id]?.bufRate !== undefined 
                ? customRates[c.id].bufRate 
                : (delInfo?.buf_rate !== undefined && delInfo?.buf_rate !== null ? parseFloat(delInfo.buf_rate) : baseBufRate);

              const cowLiters = parseFloat(curCow || 0);
              const bufLiters = parseFloat(curBuf || 0);
              const cowAmt = Math.round(cowLiters * activeCowRate * 100) / 100;
              const bufAmt = Math.round(bufLiters * activeBufRate * 100) / 100;

              // Today's total delivery amount (Milk + Extras)
              const extraSum = (delInfo?.extraDairy && delInfo.extraDairy.length > 0)
                ? delInfo.extraDairy.reduce((acc, it) => acc + ((it.qty || 1) * (it.rate || 0)), 0)
                : 0;
              const todayDeliveryTotal = Math.round((cowAmt + bufAmt + extraSum) * 100) / 100;

              // Live Accurate Due Balance as of today
              const effectiveDue = liveBalances[c.id] !== undefined
                ? Math.round(liveBalances[c.id])
                : (customerDues[c.id] !== undefined ? Math.round(customerDues[c.id]) : (parseFloat(c.opening_balance || c.balance || 0)));
              
              // Safety & Precision Running Balance Calculation:
              // Guarantees that today's delivery is never zeroed out when effectiveDue is 0 or less than today's delivery
              let priorDue, currentDueAfterDelivery;

              if (isDelivered) {
                if (effectiveDue >= todayDeliveryTotal && todayDeliveryTotal > 0) {
                  // Backend already includes today's delivery in effectiveDue
                  priorDue = Math.round((effectiveDue - todayDeliveryTotal) * 100) / 100;
                  currentDueAfterDelivery = effectiveDue;
                } else if (effectiveDue >= 0) {
                  // effectiveDue did not yet include today's delivery (e.g. effectiveDue is 0)
                  priorDue = effectiveDue;
                  currentDueAfterDelivery = Math.round((effectiveDue + todayDeliveryTotal) * 100) / 100;
                } else {
                  // Customer has advance credit (negative balance)
                  const backendAlreadyIncluded = liveBalances[c.id] !== undefined || (initialDbDeliveredRef.current && initialDbDeliveredRef.current.has(c.id));
                  if (backendAlreadyIncluded) {
                    priorDue = Math.round((effectiveDue - todayDeliveryTotal) * 100) / 100;
                    currentDueAfterDelivery = effectiveDue;
                  } else {
                    priorDue = effectiveDue;
                    currentDueAfterDelivery = Math.round((effectiveDue + todayDeliveryTotal) * 100) / 100;
                  }
                }
              } else {
                // Pending / not delivered yet
                priorDue = effectiveDue;
                currentDueAfterDelivery = Math.round((effectiveDue + todayDeliveryTotal) * 100) / 100;
              }

              // Format date as DD/MM/YYYY
              const [sy, sm, sd] = (selectedDate || todayStr).split('-');
              const formattedDateStr = (sy && sm && sd) ? `${sd}/${sm}/${sy}` : (selectedDate || todayStr);

              // Phone, SMS & WhatsApp message preparation
              const cleanPhone = (c.phone || '').replace(/\D/g, '');
              const dairyBrand = currentTenant?.brand_name_mr || currentTenant?.brandMarathi || currentTenant?.name || 'समर्थ कृपा डेअरी';

              // Assembling text lines
              let msgLines = [];
              msgLines.push(dairyBrand);
              msgLines.push(`दिनांक : ${formattedDateStr}`);

              // Buffalo Milk line
              if (bufLiters > 0 || (bufQuota > 0 && cowLiters === 0)) {
                msgLines.push(`म्हैस लि: ${bufLiters}`);
                msgLines.push(`म्हैस दर: ${activeBufRate}`);
                msgLines.push(`म्हैस रक्कम रु.: ${bufAmt.toFixed(2)}`);
              }

              // Cow Milk line
              if (cowLiters > 0 || (cowQuota > 0 && bufLiters === 0)) {
                msgLines.push(`गायी लि: ${cowLiters}`);
                msgLines.push(`गायी दर: ${activeCowRate}`);
                msgLines.push(`गायी रक्कम रु.: ${cowAmt.toFixed(2)}`);
              }

              // Extra Dairy Items
              if (delInfo?.extraDairy && delInfo.extraDairy.length > 0) {
                msgLines.push(`अतिरिक्त वस्तू रक्कम रु.: ${extraSum.toFixed(2)}`);
              }

              // If 0 liters
              if (bufLiters === 0 && cowLiters === 0 && (!delInfo?.extraDairy || delInfo.extraDairy.length === 0)) {
                msgLines.push(`आजचे वाटप: 0 लि`);
              }

              // Final Due Line: Exact match with card display (includes today's milk delivery)
              const finalDueForMsg = (todayDeliveryTotal > 0 || isDelivered) ? currentDueAfterDelivery : priorDue;
              if (finalDueForMsg < 0) {
                msgLines.push(`आज अखेर जमा शिल्लक : ${Math.abs(finalDueForMsg)}`);
              } else {
                msgLines.push(`आज अखेर येणे बाकी : ${finalDueForMsg}`);
              }

              const deliveryWaText = msgLines.join('\n');
              const deliverySmsText = msgLines.join('\n');

              // Line name resolution
              const cLine = lines.find(l => l.id.toString() === (c.line_id || c.lineId || '').toString());
              const lineLabel = cLine ? (cLine.name_mr || cLine.name) : 'मार्ग';

              const showCow = cowQuota > 0 || curCow > 0 || (cowQuota === 0 && bufQuota === 0);
              const showBuf = bufQuota > 0 || curBuf > 0 || (cowQuota === 0 && bufQuota === 0);

              return (
                <div
                  key={c.id}
                  style={{
                    background: isDelivered ? '#f0fdf4' : (isAbsent ? '#fff5f5' : '#ffffff'),
                    borderRadius: 14,
                    padding: '10px 12px',
                    border: isDelivered ? '1.5px solid #86efac' : (isAbsent ? '1.5px solid #fca5a5' : '1.5px solid #cbd5e1'),
                    borderLeft: isDelivered ? '5px solid #16a34a' : (isAbsent ? '5px solid #ef4444' : '5px solid #0284c7'),
                    boxShadow: isDelivered 
                      ? '0 4px 10px rgba(22, 163, 74, 0.12)' 
                      : (isAbsent ? '0 4px 10px rgba(239, 68, 68, 0.1)' : '0 4px 10px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.04)'),
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    transition: 'all 0.18s ease'
                  }}
                >
                  {/* Card Top Row: Sequence Badge + Name + Phone / WA Actions */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{
                        background: isAbsent ? '#ef4444' : (isDelivered ? '#16a34a' : '#0284c7'),
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: '0.70rem',
                        padding: '2px 7px',
                        borderRadius: 5,
                        fontFamily: 'monospace'
                      }}>
                        #{String(c.delivery_sequence || idx + 1).padStart(2, '0')} • {lineLabel}
                      </span>
                      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a', marginTop: 2, lineHeight: 1.25 }}>
                        {c.name_mr || c.name}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 1 }}>
                        {c.address || 'पत्ता उपलब्ध नाही'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          padding: '2px 7px',
                          borderRadius: 6,
                          background: priorDue > 0 ? '#fef3c7' : (priorDue < 0 ? '#ecfdf5' : '#f1f5f9'),
                          color: priorDue > 0 ? '#b45309' : (priorDue < 0 ? '#047857' : '#64748b'),
                          border: priorDue > 0 ? '1px solid #fde68a' : '1px solid #e2e8f0'
                        }}>
                          {priorDue > 0 ? `मागील येणे: ₹${priorDue}` : (priorDue < 0 ? `अगाऊ शिल्लक: ₹${Math.abs(priorDue)}` : `मागील बाकी: ₹०`)}
                        </span>

                        {todayDeliveryTotal > 0 && (
                          <span style={{
                            fontSize: '0.76rem',
                            fontWeight: 900,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: currentDueAfterDelivery > 0 ? '#fef08a' : '#ecfdf5',
                            color: currentDueAfterDelivery > 0 ? '#854d0e' : '#047857',
                            border: currentDueAfterDelivery > 0 ? '1.5px solid #facc15' : '1.5px solid #a7f3d0',
                            boxShadow: '0 1px 3px rgba(234, 179, 8, 0.2)'
                          }}>
                            {currentDueAfterDelivery < 0 
                              ? `आज अखेर जमा शिल्लक : रु. ${Math.abs(currentDueAfterDelivery)}` 
                              : `आज अखेर येणे बाकी : रु. ${currentDueAfterDelivery}`}
                          </span>
                        )}

                        <button
                          type="button"
                          title={isMr ? "या तारखेपर्यंतचा अचूक बॅलेन्स डेटाबेसमधून तपासा" : "Check live balance up to selected date"}
                          onClick={(e) => { e.stopPropagation(); checkLiveCustomerBalance(c.id, selectedDate); }}
                          disabled={checkingBalId === c.id}
                          style={{
                            padding: '2px 8px',
                            borderRadius: 6,
                            border: '1px solid #cbd5e1',
                            background: '#ffffff',
                            color: '#0284c7',
                            fontSize: '0.72rem',
                            fontWeight: 800,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                          }}
                        >
                          <span>🔄</span>
                          <span>{checkingBalId === c.id ? (isMr ? 'तपासत आहे...' : 'Checking...') : (isMr ? 'चेक बॅलेन्स' : 'Check Balance')}</span>
                        </button>

                        {isDynamicCustomer && (
                          <span style={{
                            fontSize: '0.66rem',
                            fontWeight: 800,
                            padding: '1px 5px',
                            borderRadius: 4,
                            background: '#e0f2fe',
                            color: '#0369a1',
                            border: '1px solid #bae6fd'
                          }}>
                            दैनिक दर
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Phone, Plain SMS, WhatsApp & ₹ जमा Action Icons */}
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      {/* Quick Payment Button (Visible ONLY if user has payment rights) */}
                      {canAcceptPayments && (
                        <button
                          type="button"
                          onClick={() => {
                            openPaymentModalForCustomer(c);
                            setPaymentCash('');
                            setPaymentUpi('');
                            setPaymentNotes('');
                            setPaymentDate(selectedDate || todayStr);
                            setPaymentSuccessData(null);
                          }}
                          style={{
                            height: 32,
                            padding: '0 8px',
                            borderRadius: 8,
                            border: '1.5px solid #16a34a',
                            background: '#f0fdf4',
                            color: '#15803d',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                            fontSize: '0.76rem',
                            fontWeight: 900,
                            cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(22, 163, 74, 0.15)'
                          }}
                          title={isMr ? "ग्राहकाचे रोख किंवा UPI पेमेंट जमा करा" : "Record Customer Payment"}
                        >
                          <span style={{ fontSize: '0.82rem' }}>₹</span>
                          <span>{isMr ? 'जमा' : 'Pay'}</span>
                        </button>
                      )}
                      {cleanPhone ? (
                        <>
                          {/* 1. Phone Call */}
                          <a
                            href={`tel:${cleanPhone}`}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              border: '1px solid #cbd5e1',
                              background: '#f8fafc',
                              color: '#0284c7',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.9rem',
                              textDecoration: 'none'
                            }}
                            title={isMr ? "कॉल करा" : "Call Customer"}
                          >
                            📞
                          </a>

                          {/* 2. Plain Text SMS */}
                          <a
                            href={`sms:${cleanPhone}?body=${encodeURIComponent(deliverySmsText)}`}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              border: isDelivered ? '1.5px solid #93c5fd' : '1px solid #cbd5e1',
                              background: isDelivered ? '#eff6ff' : '#f8fafc',
                              color: isDelivered ? '#2563eb' : '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.88rem',
                              textDecoration: 'none'
                            }}
                            title={isMr ? "साधा TEXT SMS मेसेज पाठवा" : "Send SMS"}
                          >
                            💬
                          </a>

                          {/* 3. WhatsApp Message */}
                          <a
                            href={`https://wa.me/91${cleanPhone}?text=${encodeURIComponent(deliveryWaText)}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              border: isDelivered ? '1.5px solid #86efac' : '1px solid #bbf7d0',
                              background: isDelivered ? '#ecfdf5' : '#f0fdf4',
                              color: '#16a34a',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.95rem',
                              textDecoration: 'none'
                            }}
                            title={isMr ? "WhatsApp मेसेज पाठवा" : "WhatsApp Message"}
                          >
                            <span style={{ color: '#16a34a' }}>🟢</span>
                          </a>
                        </>
                      ) : (
                        <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>फोन नाही</span>
                      )}
                    </div>
                  </div>

                  {/* Product Quota & POS Edit Stepper Bar */}
                  <div style={{
                    background: '#f8fafc',
                    borderRadius: 10,
                    padding: '8px 10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: '1px solid #e2e8f0',
                    flexWrap: 'wrap',
                    gap: 10
                  }}>
                    {isAbsent ? (
                      <span style={{ color: '#dc2626', fontWeight: 800, fontSize: '0.84rem' }}>
                        🚫 आज खाडा नोंदवला
                      </span>
                    ) : canEditQty ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        {/* Cow Milk Stepper: गाय QTY [-] [0.2] [+] */}
                        {showCow && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.80rem', fontWeight: 800, color: '#0369a1', whiteSpace: 'nowrap' }}>
                              गाय QTY
                            </span>
                            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleStepQty(c, 'cow', -0.5)}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '6px 0 0 6px',
                                  border: 'none',
                                  background: '#22c55e',
                                  color: '#ffffff',
                                  fontWeight: 900,
                                  fontSize: '1.05rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0
                                }}
                                title="-0.5 L"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                step="any"
                                min="0"
                                value={curCow}
                                onChange={(e) => handleDirectQtyChange(c, 'cow', e.target.value)}
                                style={{
                                  width: 50,
                                  height: 28,
                                  border: '1.5px solid #22c55e',
                                  borderLeft: 'none',
                                  borderRight: 'none',
                                  background: '#ffffff',
                                  textAlign: 'center',
                                  fontSize: '0.88rem',
                                  fontWeight: 800,
                                  color: '#0f172a',
                                  outline: 'none',
                                  padding: '0 2px'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => handleStepQty(c, 'cow', 0.5)}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '0 6px 6px 0',
                                  border: 'none',
                                  background: '#22c55e',
                                  color: '#ffffff',
                                  fontWeight: 900,
                                  fontSize: '1.05rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0
                                }}
                                title="+0.5 L"
                              >
                                +
                              </button>
                            </div>

                            {/* Dynamic Cow Rate Input */}
                            {isDynamicCustomer && canModifyRate && (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 2 }} title="आजचा गायीचा दूध दर बदला">
                                <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#0369a1' }}>दर ₹</span>
                                <input
                                  type="number"
                                  step="1"
                                  value={activeCowRate}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setCustomRates(prev => ({
                                      ...prev,
                                      [c.id]: { ...(prev[c.id] || {}), cowRate: val }
                                    }));
                                  }}
                                  style={{
                                    width: 46,
                                    height: 26,
                                    padding: '0 3px',
                                    fontSize: '0.82rem',
                                    fontWeight: 900,
                                    color: '#0369a1',
                                    border: '1.5px solid #0284c7',
                                    borderRadius: 6,
                                    textAlign: 'center',
                                    background: '#f0f9ff',
                                    outline: 'none'
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}

                        {/* Buffalo Milk Stepper: म्हैस QTY [-] [1.5] [+] */}
                        {showBuf && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.80rem', fontWeight: 800, color: '#4338ca', whiteSpace: 'nowrap' }}>
                              म्हैस QTY
                            </span>
                            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleStepQty(c, 'buf', -0.5)}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '6px 0 0 6px',
                                  border: 'none',
                                  background: '#22c55e',
                                  color: '#ffffff',
                                  fontWeight: 900,
                                  fontSize: '1.05rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0
                                }}
                                title="-0.5 L"
                              >
                                -
                              </button>
                              <input
                                type="number"
                                step="any"
                                min="0"
                                value={curBuf}
                                onChange={(e) => handleDirectQtyChange(c, 'buf', e.target.value)}
                                style={{
                                  width: 50,
                                  height: 28,
                                  border: '1.5px solid #22c55e',
                                  borderLeft: 'none',
                                  borderRight: 'none',
                                  background: '#ffffff',
                                  textAlign: 'center',
                                  fontSize: '0.88rem',
                                  fontWeight: 800,
                                  color: '#0f172a',
                                  outline: 'none',
                                  padding: '0 2px'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => handleStepQty(c, 'buf', 0.5)}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '0 6px 6px 0',
                                  border: 'none',
                                  background: '#22c55e',
                                  color: '#ffffff',
                                  fontWeight: 900,
                                  fontSize: '1.05rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0
                                }}
                                title="+0.5 L"
                              >
                                +
                              </button>
                            </div>

                            {/* Dynamic Buffalo Rate Input */}
                            {isDynamicCustomer && canModifyRate && (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 2 }} title="आजचा म्हशीचा दूध दर बदला">
                                <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#4338ca' }}>दर ₹</span>
                                <input
                                  type="number"
                                  step="1"
                                  value={activeBufRate}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setCustomRates(prev => ({
                                      ...prev,
                                      [c.id]: { ...(prev[c.id] || {}), bufRate: val }
                                    }));
                                  }}
                                  style={{
                                    width: 46,
                                    height: 26,
                                    padding: '0 3px',
                                    fontSize: '0.82rem',
                                    fontWeight: 900,
                                    color: '#4338ca',
                                    border: '1.5px solid #6366f1',
                                    borderRadius: 6,
                                    textAlign: 'center',
                                    background: '#eef2ff',
                                    outline: 'none'
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {curCow > 0 && (
                          <span style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0369a1' }}>
                            🥛 गाय: <strong>{curCow} L</strong>
                          </span>
                        )}
                        {curBuf > 0 && (
                          <span style={{ fontSize: '0.88rem', fontWeight: 800, color: '#4338ca' }}>
                            🥛 म्हैस: <strong>{curBuf} L</strong>
                          </span>
                        )}
                        {curCow === 0 && curBuf === 0 && (
                          <span style={{ fontSize: '0.78rem', color: '#64748b' }}>दूध नाही</span>
                        )}
                      </div>
                    )}

                    {/* Extra items preview tag */}
                    {hasExtraProducts && delInfo.extraDairy && delInfo.extraDairy.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {delInfo.extraDairy.map((it, i) => (
                          <span key={i} style={{ background: '#fef3c7', color: '#b45309', fontSize: '0.70rem', fontWeight: 800, padding: '2px 7px', borderRadius: 5, border: '1px solid #fde68a' }}>
                            🧀 {it.name} (x{it.qty})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 1-Line 2-Button Grid (✅ वाटप, ❌ खाडा) - (+ जादा button removed) */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 8, marginTop: 2 }}>
                    <button
                      type="button"
                      onClick={() => toggleDelivered(c)}
                      style={{
                        height: 38,
                        borderRadius: 10,
                        border: 'none',
                        background: isDelivered ? '#16a34a' : '#22c55e',
                        color: '#ffffff',
                        fontWeight: 800,
                        fontSize: '0.86rem',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span style={{ fontSize: '1rem' }}>✅</span>
                      <span>{isDelivered ? (isMr ? 'झाले' : 'Delivered') : (isMr ? 'वाटप' : 'Deliver')}</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => markAbsent(c)}
                      style={{
                        height: 38,
                        borderRadius: 10,
                        border: '1.5px solid #fecaca',
                        background: isAbsent ? '#fee2e2' : '#f8fafc',
                        color: '#dc2626',
                        fontWeight: 800,
                        fontSize: '0.86rem',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span style={{ fontSize: '1rem' }}>❌</span>
                      <span>{isAbsent ? (isMr ? 'खाडा नोंदवला' : 'Absent') : (isMr ? 'खाडा' : 'Absent')}</span>
                    </button>
                  </div>

                </div>
              );
            })
          )}
        </div>
      )}

      {/* ============================================================
          TAB 2: ORDERS (आजचे बाय-प्रॉडक्ट्स व बेकरी ऑर्डर्स)
          ============================================================ */}
      {deliveryTab === 'orders' && (
        <div style={{ padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: '#ffffff', borderRadius: 14, padding: '14px', border: '1.5px solid #cbd5e1', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>
              🧀 आजचे विशेष बाय-प्रॉडक्ट्स व बेकरी वाटप
            </h3>
            <p style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: 12 }}>
              दुधाव्यतिरिक्त आज ग्राहकांनी मागवलेली इतर उत्पादने (पनीर, तूप, लोणी, बेकरी इ.):
            </p>

            {customersWithExtraOrders.length === 0 ? (
              <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: 10 }}>
                {isMr ? 'आज या मार्गावर कोणत्याही अतिरिक्त उत्पादनांची मागणी नाही.' : 'No extra product orders for today on this route.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {customersWithExtraOrders.map(c => {
                  const del = deliveryMap[c.id];
                  const isDel = del?.status === 'Delivered';
                  return (
                    <div key={c.id} style={{ background: '#f8fafc', padding: '10px', border: '1px solid #e2e8f0', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a' }}>{c.name_mr || c.name}</div>
                        <div style={{ fontSize: '0.74rem', color: '#0284c7', fontWeight: 700, marginTop: 2 }}>
                          {del.extraDairy.map(it => `${it.name} x${it.qty} (₹${(it.qty * it.rate).toFixed(0)})`).join(', ')}
                        </div>
                        <div style={{ fontSize: '0.70rem', color: '#64748b' }}>{c.address}</div>
                      </div>
                      <span style={{
                        background: isDel ? '#dcfce7' : '#fef9c3',
                        color: isDel ? '#15803d' : '#a16207',
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        padding: '3px 8px',
                        borderRadius: 6
                      }}>
                        {isDel ? 'देऊन झाले' : 'बाकी'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================================
          TAB 3: SUMMARY (आजचा ताळेबंद / हिशोब)
          ============================================================ */}
      {deliveryTab === 'summary' && (
        <div style={{ padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ background: '#0284c7', color: '#fff', padding: '14px', borderRadius: 14, boxShadow: '0 4px 10px rgba(2, 132, 199, 0.25)' }}>
            <div style={{ fontSize: '0.74rem', opacity: 0.9 }}>{shift === 'Morning' ? '🌅 सकाळ शिफ्ट' : '🌙 संध्याकाळ शिफ्ट'} • ताळेबंद सारांश</div>
            <div style={{ fontSize: '1.45rem', fontWeight: 900, marginTop: 2 }}>
              {(totalCowDelivered + totalBufDelivered).toFixed(1)} L एकूण वाटप झाले
            </div>
            <div style={{ fontSize: '0.74rem', marginTop: 4, opacity: 0.9 }}>
              {deliveredCount} ग्राहक पूर्ण | {pendingCount} प्रलंबित | {absentCount} खाडे
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ background: '#ffffff', borderRadius: 14, padding: '12px', border: '1.5px solid #cbd5e1', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>🐄 गाय दूध</div>
              <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0284c7', marginTop: 4 }}>
                {totalCowDelivered.toFixed(1)} L
              </div>
              <div style={{ fontSize: '0.68rem', color: '#16a34a', fontWeight: 700, marginTop: 2 }}>वितरित झाले</div>
            </div>

            <div style={{ background: '#ffffff', borderRadius: 14, padding: '12px', border: '1.5px solid #cbd5e1', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>🐃 म्हैस दूध</div>
              <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#0f172a', marginTop: 4 }}>
                {totalBufDelivered.toFixed(1)} L
              </div>
              <div style={{ fontSize: '0.68rem', color: '#16a34a', fontWeight: 700, marginTop: 2 }}>वितरित झाले</div>
            </div>

            <div style={{ background: '#ffffff', borderRadius: 14, padding: '12px', border: '1.5px solid #cbd5e1', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>🧀 बाय-प्रॉडक्ट्स विक्री</div>
              <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#b45309', marginTop: 4 }}>
                ₹{totalByproductRevenue.toFixed(0)}
              </div>
              <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2 }}>पनीर, तूप, बेकरी इ.</div>
            </div>

            <div style={{ background: '#ffffff', borderRadius: 14, padding: '12px', border: '1.5px solid #cbd5e1', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700 }}>👥 ग्राहक स्थिती</div>
              <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#16a34a', marginTop: 4 }}>
                {deliveredCount} / {filteredCustomers.length}
              </div>
              <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2 }}>वाटप पूर्ण</div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          TAB 4: PROFILE (प्रोफाईल व सोपे लॉगआउट)
          ============================================================ */}
      {deliveryTab === 'profile' && (
        <div style={{ padding: '14px 10px' }}>
          <div style={{ background: '#ffffff', borderRadius: 16, padding: '20px 16px', border: '1.5px solid #cbd5e1', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.06)' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#e0f2fe', color: '#0284c7', fontSize: '1.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              🛵
            </div>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 900, color: '#0f172a', margin: '10px 0 2px 0' }}>
              {currentUser?.name || 'डिलिव्हरी बॉय'}
            </h3>
            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
              युझरनेम: @{currentUser?.username || 'delivery'}
            </div>

            <div style={{ marginTop: 16, textAlign: 'left', background: '#f8fafc', padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: 12, fontSize: '0.82rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div><strong>डेअरी:</strong> {currentTenant?.brand_name_mr || currentTenant?.name || 'MilkPro Mitra'}</div>
              <div><strong>भूमिका:</strong> डिलिव्हरी बॉय (Field Delivery)</div>
              <div><strong>हेल्पलाईन:</strong> {currentTenant?.dairy_settings?.phone || currentTenant?.phone || 'उपलब्ध नाही'}</div>
            </div>

            {onLogout && (
              <button
                onClick={onLogout}
                style={{
                  width: '100%',
                  marginTop: 18,
                  padding: '12px',
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '0.92rem',
                  fontWeight: 900,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6
                }}
              >
                <LogOut size={18} />
                <span>लॉगआउट करा (Logout)</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* ============================================================
          FIXED BOTTOM NAVIGATION BAR FOR DELIVERY BOY ROLE
          ============================================================ */}
      {isDeliveryRole && (
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 64,
          background: '#ffffff',
          borderTop: '1.5px solid #cbd5e1',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          boxShadow: '0 -4px 15px rgba(0, 0, 0, 0.08)',
          zIndex: 50,
          paddingBottom: 4
        }}>
          <button
            onClick={() => setDeliveryTab('runsheet')}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              background: 'transparent',
              border: 'none',
              color: deliveryTab === 'runsheet' ? '#0284c7' : '#64748b',
              fontSize: '0.68rem',
              fontWeight: deliveryTab === 'runsheet' ? 900 : 700,
              cursor: 'pointer',
              position: 'relative',
              padding: '5px 14px'
            }}
          >
            <Truck size={20} />
            <span>वितरण</span>
            {pendingCount > 0 && (
              <span style={{
                position: 'absolute',
                top: 0,
                right: 8,
                background: '#ef4444',
                color: '#fff',
                fontSize: '0.60rem',
                fontWeight: 900,
                borderRadius: 10,
                padding: '1px 5px'
              }}>
                {pendingCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setDeliveryTab('orders')}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              background: 'transparent',
              border: 'none',
              color: deliveryTab === 'orders' ? '#0284c7' : '#64748b',
              fontSize: '0.68rem',
              fontWeight: deliveryTab === 'orders' ? 900 : 700,
              cursor: 'pointer',
              position: 'relative',
              padding: '5px 14px'
            }}
          >
            <Package size={20} />
            <span>ऑर्डर्स</span>
            {customersWithExtraOrders.length > 0 && (
              <span style={{
                position: 'absolute',
                top: 0,
                right: 8,
                background: '#f59e0b',
                color: '#fff',
                fontSize: '0.60rem',
                fontWeight: 900,
                borderRadius: 10,
                padding: '1px 5px'
              }}>
                {customersWithExtraOrders.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setDeliveryTab('summary')}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              background: 'transparent',
              border: 'none',
              color: deliveryTab === 'summary' ? '#0284c7' : '#64748b',
              fontSize: '0.68rem',
              fontWeight: deliveryTab === 'summary' ? 900 : 700,
              cursor: 'pointer',
              padding: '5px 14px'
            }}
          >
            <BarChart3 size={20} />
            <span>ताळेबंद</span>
          </button>

          <button
            onClick={() => setDeliveryTab('profile')}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              background: 'transparent',
              border: 'none',
              color: deliveryTab === 'profile' ? '#0284c7' : '#64748b',
              fontSize: '0.68rem',
              fontWeight: deliveryTab === 'profile' ? 900 : 700,
              cursor: 'pointer',
              padding: '5px 14px'
            }}
          >
            <User size={20} />
            <span>प्रोफाईल</span>
          </button>
        </nav>
      )}

      {/* ============================================================
          EXTRA PRODUCTS / QUANTITY ADJUSTMENT BOTTOM SHEET MODAL
          ============================================================ */}
      {extraModalCust && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          zIndex: 120
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '20px 20px 0 0',
            maxWidth: 480,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 -10px 30px rgba(0,0,0,0.25)'
          }}>
            
            {/* Modal Header */}
            <div style={{ background: '#0284c7', color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ fontWeight: 900, fontSize: '1rem' }}>
                  {isMr ? (extraModalCust.name_mr || extraModalCust.name) : (extraModalCust.name_en || extraModalCust.name)}
                </span>
                <div style={{ fontSize: '0.72rem', color: '#e0f2fe', marginTop: 2 }}>
                  जादा दूध व अतिरिक्त बाय-प्रॉडक्ट्स नोंदवा
                </div>
              </div>
              <button onClick={() => setExtraModalCust(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '14px 16px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
              
              {/* Quick Milk adjustments */}
              <div style={{ background: '#f8fafc', padding: '10px', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 6 }}>
                  🥛 आजचे जादा गाय दूध:
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[0, 0.5, 1.0, 2.0].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setExtraCowDelta(val)}
                      style={{
                        flex: 1,
                        padding: '6px',
                        borderRadius: 6,
                        border: extraCowDelta === val ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                        background: extraCowDelta === val ? '#e0f2fe' : '#ffffff',
                        fontWeight: 800,
                        fontSize: '0.8rem',
                        color: extraCowDelta === val ? '#0284c7' : '#334155',
                        cursor: 'pointer'
                      }}
                    >
                      {val === 0 ? 'नको' : `+${val}L`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Byproducts selection */}
              {hasExtraProducts && extraAvailableProducts.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155' }}>
                    🧀 बाय-प्रॉडक्ट्स व बेकरी उत्पादने:
                  </label>
                  {extraAvailableProducts.map(p => {
                    const rate = parseFloat(p.retail_rate !== undefined ? p.retail_rate : (p.retailRate || 0));
                    const curQty = extraItemQtys[p.id] || 0;
                    return (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: curQty > 0 ? '#f0f9ff' : '#f8fafc', border: curQty > 0 ? '1.5px solid #0284c7' : '1px solid #e2e8f0', borderRadius: 8 }}>
                        <div>
                          <div style={{ fontWeight: 800, fontSize: '0.84rem' }}>{p.name_mr || p.name}</div>
                          <div style={{ fontSize: '0.70rem', color: '#64748b' }}>₹{rate.toFixed(0)} {p.unit_mr ? `/ ${p.unit_mr}` : ''}</div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => setItemQty(p.id, Math.max(0, curQty - 1))}
                            style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 900 }}
                          >
                            -
                          </button>
                          <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 900, fontSize: '0.9rem', color: curQty > 0 ? '#0284c7' : '#64748b' }}>
                            {curQty}
                          </span>
                          <button
                            type="button"
                            onClick={() => setItemQty(p.id, curQty + 1)}
                            style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: 900 }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

            </div>

            {/* Modal Footer */}
            <div style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setExtraModalCust(null)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#ffffff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}
              >
                रद्द करा
              </button>
              <button
                type="button"
                onClick={handleSaveExtraModal}
                style={{ padding: '8px 24px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#ffffff', fontWeight: 900, fontSize: '0.88rem', cursor: 'pointer', boxShadow: '0 2px 6px rgba(22,163,74,0.3)' }}
              >
                💾 जतन करा
              </button>
            </div>

          </div>
        </div>
      )}

    
      {/* Bulk Delete Deliveries Modal */}
      {isBulkDeleteModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            maxWidth: 440,
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)',
            border: '1.5px solid #fca5a5'
          }}>
            <div style={{ background: '#dc2626', color: '#fff', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 900, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>🗑️</span>
                <span>{isMr ? 'वाटप नोंदी रीसेट / डिलीट करा' : 'Reset / Delete Deliveries'}</span>
              </div>
              <button
                type="button"
                onClick={() => setIsBulkDeleteModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '1.1rem' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px', fontSize: '0.80rem', color: '#991b1b', lineHeight: 1.45 }}>
                ⚠️ <strong>सावधान:</strong> निवडलेल्या तारीख व मार्गावरील वाटप नोंदी कायमच्या हटवल्या जातील आणि सर्व संबंधित ग्राहकांची स्थिती पुन्हा <strong>'Pending'</strong> होईल.
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'दिनांक:' : 'Date:'}
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  disabled
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#f8fafc', fontWeight: 700 }}
                />
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'शिफ्ट निवडा:' : 'Shift:'}
                </label>
                <select
                  value={bulkDeleteShift}
                  onChange={e => setBulkDeleteShift(e.target.value)}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#ffffff', fontWeight: 700 }}
                >
                  <option value="ALL">{isMr ? 'सर्व शिफ्ट (सकाळ + संध्याकाळ)' : 'All Shifts (Morning + Evening)'}</option>
                  <option value="Morning">{isMr ? 'फक्त सकाळ (Morning Only)' : 'Morning Only'}</option>
                  <option value="Evening">{isMr ? 'फक्त संध्याकाळ (Evening Only)' : 'Evening Only'}</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#334155', display: 'block', marginBottom: 4 }}>
                  {isMr ? 'मार्ग / लाईन निवडा:' : 'Select Route / Line:'}
                </label>
                <select
                  value={bulkDeleteLineId}
                  onChange={e => setBulkDeleteLineId(e.target.value)}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#ffffff', fontWeight: 700 }}
                >
                  <option value="ALL">{isMr ? 'सर्व मार्ग (या तारखेचे सर्व वाटप)' : 'All Lines (Entire Day Deliveries)'}</option>
                  {centerLines.map(l => (
                    <option key={l.id} value={l.id.toString()}>{l.name_mr || l.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => setIsBulkDeleteModalOpen(false)}
                  style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 800, color: '#475569', cursor: 'pointer' }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleBulkDeleteDeliveries}
                  disabled={isDeletingBulk}
                  style={{ flex: 1.4, padding: '9px', borderRadius: 8, border: 'none', background: '#dc2626', fontWeight: 900, color: '#ffffff', cursor: 'pointer', boxShadow: '0 2px 6px rgba(220, 38, 38, 0.3)' }}
                >
                  {isDeletingBulk ? (isMr ? 'हटवत आहे...' : 'Deleting...') : (isMr ? 'होय, नोंदी हटवा' : 'Yes, Delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal 3: Quick Payment Collection Modal (वाटप दरम्यान पेमेंट जमा करणे) */}
      {paymentModalCust && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '12px' }}>
          <div style={{ background: '#ffffff', borderRadius: 18, width: '100%', maxWidth: 430, overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', border: '1px solid #e2e8f0' }}>
            
            {/* Modal Header */}
            <div style={{ background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)', color: '#ffffff', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '1.4rem' }}>💰</span>
                <div>
                  <div style={{ fontWeight: 900, fontSize: '1.02rem', letterSpacing: '-0.2px' }}>
                    {isMr ? 'पेमेंट / पावती नोंदणी' : 'Record Payment Receipt'}
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.92, fontWeight: 600 }}>
                    {paymentModalCust.name_mr || paymentModalCust.name} {paymentModalCust.phone ? `(${paymentModalCust.phone})` : ''}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setPaymentModalCust(null); setPaymentSuccessData(null); }}
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#ffffff', width: 32, height: 32, borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            {paymentSuccessData ? (
              /* Success Confirmation View */
              <div style={{ padding: '22px 18px', textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#dcfce7', color: '#16a34a', fontSize: '1.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  ✓
                </div>
                <div style={{ fontSize: '1.15rem', fontWeight: 900, color: '#166534', marginBottom: 4 }}>
                  {isMr ? 'पेमेंट यशस्वीपणे जमा झाले!' : 'Payment Recorded Successfully!'}
                </div>
                <div style={{ fontSize: '0.86rem', color: '#475569', marginBottom: 16 }}>
                  {paymentSuccessData.cust.name_mr || paymentSuccessData.cust.name}
                </div>

                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18, textAlign: 'left' }}>
                  <div>
                    <div style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 700 }}>{isMr ? 'मिळालेली एकूण रक्कम' : 'Total Received'}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 900, color: '#16a34a' }}>₹{paymentSuccessData.totalAmt}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.74rem', color: '#64748b', fontWeight: 700 }}>{isMr ? 'उर्वरित बाकी शिल्लक' : 'Remaining Due'}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 900, color: paymentSuccessData.updatedDue > 0 ? '#b45309' : '#16a34a' }}>₹{paymentSuccessData.updatedDue}</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                  <button
                    type="button"
                    onClick={handleSendWhatsAppReceipt}
                    style={{
                      padding: '11px',
                      borderRadius: 10,
                      border: 'none',
                      background: '#25D366',
                      color: '#ffffff',
                      fontWeight: 900,
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      boxShadow: '0 2px 8px rgba(37, 211, 102, 0.35)'
                    }}
                  >
                    <span>💬 WhatsApp पावती</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSendSmsReceipt}
                    style={{
                      padding: '11px',
                      borderRadius: 10,
                      border: 'none',
                      background: '#0284c7',
                      color: '#ffffff',
                      fontWeight: 900,
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      boxShadow: '0 2px 8px rgba(2, 132, 199, 0.35)'
                    }}
                  >
                    <span>✉️ SMS पावती</span>
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => { setPaymentModalCust(null); setPaymentSuccessData(null); }}
                  style={{
                    width: '100%',
                    padding: '11px',
                    borderRadius: 10,
                    border: '1px solid #cbd5e1',
                    background: '#ffffff',
                    fontWeight: 800,
                    color: '#334155',
                    cursor: 'pointer',
                    fontSize: '0.88rem'
                  }}
                >
                  {isMr ? 'पूर्ण करा (Done)' : 'Close'}
                </button>
              </div>
            ) : (
              /* Entry Form View */
              <form onSubmit={handleRecordPayment} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                
                {/* Outstanding Dues Banner */}
                <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 12, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#92400e' }}>
                    {isMr ? 'आज अखेर येणे बाकी:' : 'Current Outstanding Due:'}
                  </span>
                  <span style={{ fontSize: '1.25rem', fontWeight: 900, color: '#b45309', fontFamily: 'monospace' }}>
                    ₹{paymentCustDue}
                  </span>
                </div>

                {/* Quick Presets */}
                <div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {paymentCustDue > 0 && (
                      <button
                        type="button"
                        onClick={() => { setPaymentCash(paymentCustDue.toString()); setPaymentUpi(''); }}
                        style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #16a34a', background: '#f0fdf4', color: '#15803d', fontSize: '0.74rem', fontWeight: 800, cursor: 'pointer' }}
                      >
                        {isMr ? `पूर्ण बाकी रोख (₹${paymentCustDue})` : `Full Cash (₹${paymentCustDue})`}
                      </button>
                    )}
                    {paymentCustDue > 0 && (
                      <button
                        type="button"
                        onClick={() => { setPaymentUpi(paymentCustDue.toString()); setPaymentCash(''); }}
                        style={{ padding: '4px 10px', borderRadius: 8, border: '1px solid #0284c7', background: '#f0f9ff', color: '#0369a1', fontSize: '0.74rem', fontWeight: 800, cursor: 'pointer' }}
                      >
                        {isMr ? `पूर्ण बाकी UPI (₹${paymentCustDue})` : `Full UPI (₹${paymentCustDue})`}
                      </button>
                    )}
                    {[500, 1000, 2000].map(amt => (
                      <button
                        key={amt}
                        type="button"
                        onClick={() => {
                          const current = parseFloat(paymentCash) || 0;
                          setPaymentCash((current + amt).toString());
                        }}
                        style={{ padding: '4px 8px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontSize: '0.74rem', fontWeight: 800, cursor: 'pointer' }}
                      >
                        + ₹{amt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* TWO INPUTS ONLY: Cash and UPI */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {/* Cash Input */}
                  <div style={{ background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: 12, padding: '10px' }}>
                    <label style={{ fontSize: '0.76rem', fontWeight: 900, color: '#166534', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
                      <span>💵</span> {isMr ? 'रोख रक्कम (Cash):' : 'Cash Amount:'}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontWeight: 900, color: '#64748b' }}>₹</span>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        value={paymentCash}
                        onChange={e => setPaymentCash(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 8px 8px 24px',
                          borderRadius: 8,
                          border: paymentCashNum > 0 ? '2px solid #16a34a' : '1px solid #cbd5e1',
                          fontSize: '1.15rem',
                          fontWeight: 900,
                          color: '#15803d',
                          boxSizing: 'border-box',
                          background: '#ffffff'
                        }}
                      />
                    </div>
                  </div>

                  {/* UPI Input */}
                  <div style={{ background: '#f8fafc', border: '1.5px solid #cbd5e1', borderRadius: 12, padding: '10px' }}>
                    <label style={{ fontSize: '0.76rem', fontWeight: 900, color: '#0369a1', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
                      <span>📱</span> {isMr ? 'UPI रक्कम:' : 'UPI Amount:'}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontWeight: 900, color: '#64748b' }}>₹</span>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        value={paymentUpi}
                        onChange={e => setPaymentUpi(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 8px 8px 24px',
                          borderRadius: 8,
                          border: paymentUpiNum > 0 ? '2px solid #0284c7' : '1px solid #cbd5e1',
                          fontSize: '1.15rem',
                          fontWeight: 900,
                          color: '#0369a1',
                          boxSizing: 'border-box',
                          background: '#ffffff'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Dynamic Calculation Card: Total Received & Remaining Balance */}
                <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)', border: '1.5px solid #86efac', borderRadius: 12, padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#166534', marginBottom: 2 }}>
                      {isMr ? 'मिळालेली एकूण रक्कम' : 'Total Received'}
                    </div>
                    <div style={{ fontSize: '1.35rem', fontWeight: 900, color: '#15803d', fontFamily: 'monospace' }}>
                      ₹{paymentTotalReceived}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#92400e', marginBottom: 2 }}>
                      {isMr ? 'उर्वरित बाकी शिल्लक' : 'Remaining Due'}
                    </div>
                    <div style={{ fontSize: '1.35rem', fontWeight: 900, color: paymentRemainingBalance > 0 ? '#b45309' : '#15803d', fontFamily: 'monospace' }}>
                      ₹{paymentRemainingBalance}
                    </div>
                  </div>
                </div>

                {/* Compact Date & Note */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: '0.72rem', fontWeight: 800, color: '#475569', display: 'block', marginBottom: 3 }}>
                      {isMr ? 'पावती दिनांक:' : 'Date:'}
                    </label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="date"
                        value={paymentDate}
                        onChange={e => {
                          const nd = e.target.value;
                          setPaymentDate(nd);
                          if (paymentModalCust) {
                            checkLiveCustomerBalance(paymentModalCust.id, nd);
                          }
                        }}
                        style={{ width: '100%', padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                      />
                      <button
                        type="button"
                        onClick={() => checkLiveCustomerBalance(paymentModalCust.id, paymentDate)}
                        disabled={checkingBalId === paymentModalCust.id}
                        title={isMr ? "या पावती तारखेचा चालू बॅलेन्स तपासा" : "Check balance for this date"}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: '1.5px solid #0284c7',
                          background: '#f0f9ff',
                          color: '#0369a1',
                          fontWeight: 800,
                          fontSize: '0.76rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <span>🔄</span>
                        <span>{checkingBalId === paymentModalCust.id ? '...' : (isMr ? 'चेक बॅलेन्स' : 'Check')}</span>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.72rem', fontWeight: 800, color: '#475569', display: 'block', marginBottom: 3 }}>
                      {isMr ? 'UTR / नोंद:' : 'Ref / Note:'}
                    </label>
                    <input
                      type="text"
                      placeholder={isMr ? "उदा. GPay Ref" : "Optional ref"}
                      value={paymentNotes}
                      onChange={e => setPaymentNotes(e.target.value)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* Instant Send SMS & WhatsApp Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 2 }}>
                  <button
                    type="button"
                    onClick={handleSendWhatsAppReceipt}
                    disabled={paymentTotalReceived <= 0}
                    style={{
                      padding: '9px 8px',
                      borderRadius: 10,
                      border: '1px solid #25D366',
                      background: paymentTotalReceived > 0 ? '#f0fdf4' : '#f8fafc',
                      color: paymentTotalReceived > 0 ? '#166534' : '#94a3b8',
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      cursor: paymentTotalReceived > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 5
                    }}
                  >
                    <span>💬 WhatsApp पावती</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSendSmsReceipt}
                    disabled={paymentTotalReceived <= 0}
                    style={{
                      padding: '9px 8px',
                      borderRadius: 10,
                      border: '1px solid #0284c7',
                      background: paymentTotalReceived > 0 ? '#f0f9ff' : '#f8fafc',
                      color: paymentTotalReceived > 0 ? '#0369a1' : '#94a3b8',
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      cursor: paymentTotalReceived > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 5
                    }}
                  >
                    <span>✉️ SMS पावती</span>
                  </button>
                </div>

                {/* Action Buttons: Save & Cancel */}
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button
                    type="button"
                    onClick={() => { setPaymentModalCust(null); setPaymentSuccessData(null); }}
                    style={{
                      flex: 1,
                      padding: '11px',
                      borderRadius: 10,
                      border: '1px solid #cbd5e1',
                      background: '#f8fafc',
                      fontWeight: 800,
                      color: '#475569',
                      cursor: 'pointer',
                      fontSize: '0.86rem'
                    }}
                  >
                    {isMr ? 'रद्द करा' : 'Cancel'}
                  </button>
                  <button
                    type="submit"
                    disabled={submittingPayment || paymentTotalReceived <= 0}
                    style={{
                      flex: 1.6,
                      padding: '11px',
                      borderRadius: 10,
                      border: 'none',
                      background: paymentTotalReceived > 0
                        ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                        : '#94a3b8',
                      fontWeight: 900,
                      color: '#ffffff',
                      cursor: (submittingPayment || paymentTotalReceived <= 0) ? 'not-allowed' : 'pointer',
                      boxShadow: paymentTotalReceived > 0 ? '0 3px 10px rgba(22, 163, 74, 0.35)' : 'none',
                      fontSize: '0.88rem'
                    }}
                  >
                    {submittingPayment ? (isMr ? 'नोंद होत आहे...' : 'Saving...') : (isMr ? '✓ पेमेंट जमा करा' : 'Confirm Payment')}
                  </button>
                </div>

              </form>
            )}

          </div>
        </div>
      )}

</div>
    );
}
